import { Calendar, FileText, MessageSquare, Database } from "lucide-react";
import { Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface UseCase {
  icon: LucideIcon;
  title: string;
  description: string;
  example: string;
}

const useCases: UseCase[] = [
  {
    icon: Calendar,
    title: "Sprint Planning",
    description: "Review user stories before backlog grooming. Know exactly what engineers will ask.",
    example: '"Is this story ready for dev?"',
  },
  {
    icon: FileText,
    title: "PRD Handoff",
    description: "Ensure clarity before development sees it. Prevent the back-and-forth.",
    example: '"What assumptions am I missing?"',
  },
  {
    icon: MessageSquare,
    title: "Stakeholder Requests",
    description: "Translate vague asks into actionable specs. Bridge the communication gap.",
    example: '"Make it more user-friendly"',
  },
  {
    icon: Database,
    title: "Migration Projects",
    description: "Document legacy systems clearly. Capture tribal knowledge before it's lost.",
    example: '"How does this actually work?"',
  },
];

export function UseCaseCards() {
  return (
    <section className="py-20 md:py-32 relative overflow-hidden">
      {/* Background ambient */}
      <div className="absolute bottom-[20%] right-[10%] w-[500px] h-[500px] rounded-full bg-primary/[0.04] blur-[160px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Use Cases
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            Built for your workflow
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Reqqy fits into the moments where clarity matters most
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {useCases.map((useCase, index) => (
            <div
              key={useCase.title}
              className="group relative p-6 rounded-2xl glass border border-border/30 animate-fade-in transition-all duration-500 hover:border-primary/20 hover:shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.12)] hover:-translate-y-1"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-primary/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-primary/15 group-hover:shadow-[0_0_20px_-4px_hsl(var(--primary)/0.2)]">
                  <useCase.icon className="w-6 h-6 text-primary transition-transform duration-300 group-hover:scale-110" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{useCase.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{useCase.description}</p>
                <div className="text-xs text-muted-foreground/60 italic border-t border-border/30 pt-3">
                  {useCase.example}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
